import $ from "../../libs/jquery-3.3.1.min.js"
import {CustomWiggle, CustomEase, gsap} from "../../libs/gsap/all.js";
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras'
import {BaseMiniGame} from "./BaseMiniGame";
import {GameH_Item} from "./components/GameH_Item";
import {GameH_Dropzone} from "./components/GameH_Dropzone";
import {GameH_Perso} from "./components/GameH_Perso";
import {AppearingInteractiveButton} from "./components/AppearingInteractiveButton";

// Game description : Clothes - Dressing game
export class GameH extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;

    this.dragTarget = false;
    this.perso = null;
    this.itemsObjects = [];
    this.tagsBackgrounds = [];
    this.dropzoneObjects = [];
    this.isCheckingAnswer = false;
    this.foundAnswers = 0;
    this.droppedCount = 0;

    this.$screenEl = null;
  }

  async assetLoader() {
    await super.assetLoader();

    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }
  }

  async buildStage() {

    this.$screenEl = $(this.canvas).parents(".screen");

    // Enable events on the whole stage
    this.stage.hitArea = this.screen;

    this.questionTextContainer = this.$screenEl.find(".game-question");
    this.questionTextContainer.css({
      top: this.data.question.y,
      left: this.data.question.x,
    });

    const imageMargin = {x: 70, y: 80};

    // Prepare image
    this.imageContainer = new PIXI.Container();

    this.imageSprite = new PIXI.Sprite(this.assets["image"]);
    this.imageSprite.width = this.data.imageDimensions.width;
    this.imageSprite.height = this.data.imageDimensions.height;
    this.imageContainer.addChild(this.imageSprite);

    this.imageContainer.position.x = imageMargin.x;
    this.imageContainer.position.y = imageMargin.y;
    this.stage.addChild(this.imageContainer);

    this.perso = new GameH_Perso(this);
    this.stage.addChild(this.perso);

    // Prepare choices
    this.interactiveContainer = new PIXI.Container();
    this.interactiveContainer.sortableChildren = true

    // Let's add a background so we have a defined zone with specific size
    let interactiveZoneBackground = new PIXI.Graphics().rect(0, 0, this.appWidth, this.appWidth).fill({
      color: 0xff0000,
      alpha: 0
    });
    this.interactiveContainer.addChild(interactiveZoneBackground);
    this.stage.addChild(this.interactiveContainer);

    for (let i = 0; i < this.data.items.length; i++) {
      let item = new GameH_Item(this.data.items[i], this);
      this.interactiveContainer.addChild(item.getBgArea());
      this.interactiveContainer.addChild(item);
      this.itemsObjects.push(item);
      this.tagsBackgrounds.push(item.bgArea);

      item.on("pointerdown", (e) => {
        this.dragTarget = e.currentTarget; // save the target so we can keep "this" scope
        this.handleOnDragStart(e);
      }).on("pointerup", (e) => {
        this.handleOnDragEnd(e);
      }).on("pointerupoutside", (e) => {
        this.handleOnDragEnd(e);
      });
    }

    for (let i = 0; i < this.data.dropZones.length; i++) {
      let dropZone = new GameH_Dropzone(this.data.dropZones[i], this);

      this.dropzoneObjects.push(dropZone);
      this.interactiveContainer.addChild(dropZone);
    }

    // Add validation button
    const checkmark = new PIXI.Sprite(this.assets["checkmark"]);
    const checkmarkDisabled = new PIXI.Sprite(this.assets["checkmark-disabled"]);
    this.validateButton = new AppearingInteractiveButton(75, 75, checkmark, checkmarkDisabled, this.validateAnswers.bind(this), false);

    this.validateButton.position.set(1490, 800);
    this.stage.addChild(this.validateButton);

    // Add reset button
    const reset = new PIXI.Sprite(this.assets["reset"]);
    const resetDisabled = new PIXI.Sprite(this.assets["reset-disabled"]);
    this.resetButton = new AppearingInteractiveButton(75, 75, reset, resetDisabled, this.resetAnswers.bind(this), false);

    this.resetButton.position.set(1390, 800);
    this.stage.addChild(this.resetButton);

    // Used for debugging in pixijs devtool
    //globalThis.__PIXI_APP__ = this;
  }

  handleOnDragStart(e) {
    // this.dragTarget.alpha = 0.5;
    this.dragTarget.pickedUp();
    const clickPos = this.dragTarget.parent.toLocal(e.global, null);

    // place tag on mouse
    gsap.to(this.dragTarget.position, {
      x: clickPos.x,
      y: clickPos.y,
      duration: 0.05
    });

    this.stage.eventMode = 'static';
    this.stage.on("pointermove", (e) => {
      this.handleOnDrag(e);
    });
  }

  handleOnDrag(e) {
    if (this.dragTarget) {
      const clickPos = this.dragTarget.parent.toLocal(e.global, null);

      // tag follow mouse
      gsap.killTweensOf(this.dragTarget.position);
      gsap.to(this.dragTarget.position, {
        x: clickPos.x,
        y: clickPos.y,
        duration: 0.05
      });
    }
  }

  handleOnDragEnd(e) {
    if (this.dragTarget) {
      this.stage.eventMode = 'auto';
      this.stage.off("pointermove");

      this.dragTarget.released();

      // Check if we landed on the related dropzone or not
      let droppedInZone = this.checkDropZone(e.global);

      if (droppedInZone !== false) {
        this.droppedCount++;

        droppedInZone.isFilled = true;
        this.dragTarget.droppedInZone = droppedInZone;
        // Reset feedback marker
        droppedInZone.hideAnswerMarker();
        this.dragTarget.disable();
        this.dragTarget.animateDropped(droppedInZone);
        // droppedInZone.animateDropped();

        this.perso.showClothes(this.dragTarget.id);

        if (this.droppedCount > 0) {
          this.resetButton.enable();
        }

        if (this.droppedCount >= this.data.dropZones.length) {
          this.validateButton.enable();
        }
      } else {
        // reset position
        this.dragTarget.resetPosition();
        // gsap.killTweensOf(this.dragTarget.position);
        // gsap.to(this.dragTarget.position, {
        //   x: this.dragTarget.originalPosition.x,
        //   y: this.dragTarget.originalPosition.y,
        //   duration: 0.25
        // })
      }

      this.dragTarget = false;
    }
  }

  checkDropZone(cursorPos) {

    // check if we dropped in a zone
    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      if (this.dropzoneObjects[i].isFilled || this.dropzoneObjects[i].id != this.dragTarget.dropZoneId) {
        continue;
      }
      // get the global position of the drop target so we can compare with the cursor position
      let dropZoneBoundingBox = this.dropzoneObjects[i].getBoundingBox();

      if (
        (cursorPos.x >= dropZoneBoundingBox.left) &&
        (cursorPos.x <= dropZoneBoundingBox.right) &&
        (cursorPos.y >= dropZoneBoundingBox.top) &&
        (cursorPos.y <= dropZoneBoundingBox.bottom)
      ) {
        return this.dropzoneObjects[i];
      }
    }

    return false;
  }

  validateAnswers() {
    let goodAnswers = 0;

    // console.log(this.itemsObjects);
    // console.log(this.dropzoneObjects);

    for (let i = 0; i < this.itemsObjects.length; i++) {
      if (this.itemsObjects[i].droppedInZone) {
        if (!this.itemsObjects[i].data.correct) {

          // // Shake animation before placing back the tag
          // gsap.to(this.itemsObjects[i], {
          //   x: this.itemsObjects[i].position.x + 10,
          //   duration: 0.50,
          //   ease: CustomWiggle.create("wrongAnswerWiggle", {
          //     wiggles: 4,
          //     type: "easeOut"
          //   }),
          // });

          // Show wrong feedback marker
          this.itemsObjects[i].droppedInZone.showAnswerMarker(false);
          this.perso.hideClothes(this.itemsObjects[i].id);

          // Place the tag back
          // setTimeout(() => {
          this.itemsObjects[i].droppedInZone.resetState();
          this.itemsObjects[i].resetState();
          this.itemsObjects[i].enable();
          // }, 1200);
        } else {
          // Show right feedback marker
          console.log(this.itemsObjects[i].droppedInZone);
          this.itemsObjects[i].droppedInZone.showAnswerMarker(true);

          goodAnswers++;
        }
      }
    }

    if (goodAnswers >= 3) {
      // Everything is good! let's go to step 2
      this.validateButton.hide();
      this.resetButton.hide();
      this.goToConclusion();

      // this.dispatchEvent(new CustomEvent("clothes_update", {detail: this.perso.getActiveClothes()}))
      // this.complete();
    } else {
      if (goodAnswers == 0) {
        this.resetButton.disable();
      }

      this.validateButton.disable();
      this.droppedCount = goodAnswers;
    }
  }

  resetAnswers() {
    this.droppedCount = 0;
    this.validateButton.disable();
    this.resetButton.disable();

    for (let i = 0; i < this.itemsObjects.length; i++) {
      this.itemsObjects[i].resetState();
      this.itemsObjects[i].enable();
    }

    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      this.dropzoneObjects[i].resetState();

      // Reset feedback markers
      this.dropzoneObjects[i].hideAnswerMarker();
    }

    this.perso.reset();
  }

  buildBegginingTimeline() {
    this.beginningTimeline = gsap.timeline({paused: true});

    this.beginningTimeline.set(this.questionTextContainer, {
      alpha: 1.0
    });

    this.beginningTimeline.set(this.tagsBackgrounds, {
      alpha: 0.0
    });

    this.beginningTimeline.fromTo(this.$screenEl.find('.game-question'), {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.50,
      immediateRender: true
    });

    this.beginningTimeline.add(() => {
      for (let i = 0; i < this.itemsObjects.length; i++) {
        // stagger the animation
        setTimeout(() => {
          this.itemsObjects[i].animateIn();
          // this.dropzoneObjects[i].animateIn();
        }, 200 * i);
      }

      // Show validate button but disabled
      setTimeout(() => {
        this.resetButton.show(false);
      }, this.itemsObjects.length * 200 + 600);

      setTimeout(() => {
        this.validateButton.show(false);

        for (let i = 0; i < this.itemsObjects.length; i++) {
          this.itemsObjects[i].enable();
        }
      }, this.itemsObjects.length * 200 + 800);
    });
  }

  buildConclusionTimeline() {
    this.conclusionTimeline = gsap.timeline({paused: true});

    this.conclusionTimeline.addLabel("fade-out-question");

    this.conclusionTimeline.to(this.questionTextContainer, {
      alpha: 0.0,
      duration: 0.25,
      delay: 0.70,
      ease: "power1.inOut"
    }, "fade-out-question");

    this.conclusionTimeline.add(() => {
      for (let i = 0; i < this.itemsObjects.length; i++) {
        // stagger the animation
        this.itemsObjects[i].hide();
      }
    }, "fade-out-question");

    // force game conclusion to be active
    this.conclusionTimeline.set(this.$screenEl.find(".game-conclusion"), {
      alpha: 1,
    });

    if (this.$screenEl.find(".container-title").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title"), {
        '--title-width': "0%"
      }, {
        '--title-width': "100%",
        duration: 0.45,
        ease: "power2.out",
        immediateRender: true
      });

      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-title-inner"), {
        alpha: 0.0
      }, {
        alpha: 1.0,
        duration: 0.30,
        ease: "power1.in",
        immediateRender: true
      });
    }

    this.conclusionTimeline.fromTo(this.$screenEl.find('.container-content'), {
      alpha: 0,
    }, {
      alpha: 1,
      ease: "power1.inOut",
      duration: 0.30,
      immediateRender: true
    });

    this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
      y: "+=100%"
    }, {
      y: 0,
      duration: 1.50,
      ease: "power1.inOut",
      immediateRender: true
    }, "+=0.75");

    this.conclusionTimeline.fromTo(this.$screenEl.find(".container-note"), {
      autoAlpha: 0
    }, {
      autoAlpha: 1,
      duration: 0.1,
      ease: "linear",
      immediateRender: true
    }, '<=');

    // Show container button
    if (this.$screenEl.find(".container-button").length) {
      this.conclusionTimeline.fromTo(this.$screenEl.find(".container-button"), {
        alpha: 0.0,
        scale: 0.0
      }, {
        alpha: 1.0,
        scale: 1.0,
        duration: 0.75,
        ease: "power2.out",
        immediateRender: true,
        onStart: () => {
          // Disable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").removeClass("play-animation");
        },
        onComplete: () => {
          this.$screenEl.find(".game-conclusion").removeClass("disabled");

          // Enable button animations
          this.$screenEl.find("button.pulse-animation, button.up-down-animation").addClass("play-animation");
        }
      });

      // Disable click on this section
      this.$screenEl.find(".game-conclusion").addClass("disabled");
    }

  }

  goToConclusion() {
    this.conclusionTimeline.play(0);
  }

  beforeAnimateIn() {
    // rebuild timeline

    if (this.beginningTimeline) {
      this.beginningTimeline.pause();
    } else {
      this.buildBegginingTimeline();
    }

    if (this.conclusionTimeline) {
      this.conclusionTimeline.pause();
    } else {
      this.buildConclusionTimeline();
    }
  }

  begin() {
    this.beginningTimeline.play(0);
  }

  reset() {
    // console.log("gameH Reset");

    this.foundAnswers = 0;
    this.isCheckingAnswer = false;
    this.droppedCount = 0;

    this.validateButton.hide();
    this.resetButton.hide();

    for (let i = 0; i < this.itemsObjects.length; i++) {
      this.itemsObjects[i].reset();
    }

    for (let i = 0; i < this.dropzoneObjects.length; i++) {
      this.dropzoneObjects[i].reset();

      // Reset feedback markers
      this.dropzoneObjects[i].hideAnswerMarker();
    }

    this.perso.reset();

    // reset some states
    // gsap.killTweensOf([this.questionTextContainer, this.tagsBackgrounds]);
    // gsap.set([this.questionTextContainer, this.tagsBackgrounds], {
    //   alpha: 0.0
    // });

    // rebuild timeline
    if (this.beginningTimeline) {
      this.beginningTimeline.kill();
      this.beginningTimeline = null;
    }
    this.buildBegginingTimeline();

    if (this.conclusionTimeline) {
      this.conclusionTimeline.kill();
      this.conclusionTimeline = null;
    }
    this.buildConclusionTimeline();
  }

}
